import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, Menu, X, Check, MapPin, Phone, Mail, Star,
  Layout, CreditCard, ShoppingCart, Layers, Search, Rocket,
  ShieldCheck, Zap, TrendingUp, Sparkles, Plus, Volume2, VolumeX,
} from "lucide-react";
import AISearchSection from "./AISearchSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const NAV = [
  { label: "Services", href: "#services" },
  { label: "AI SEO", href: "#ai-seo" },
  { label: "Work", href: "#work" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const PLANS = [
  {
    name: "Starter",
    tagline: "Business card website",
    lookup_key: "starter_onetime",
    price: "$499",
    note: "One-time build · no monthly fee",
    features: ["1-page professional site", "Mobile-ready design", "Contact form", "Google Maps + hours", "Basic on-page SEO"],
    cta: "Get Started", popular: false,
  },
  {
    name: "Growth",
    tagline: "Multipage business site",
    lookup_key: "growth_monthly",
    price: "$99",
    priceSuffix: "/mo",
    setup: "$1,000 one-time setup",
    note: "Billed monthly · cancel anytime",
    features: ["Up to 6 custom pages", "Lead-gen contact forms", "Local SEO setup", "Fast managed hosting", "Ongoing updates & support"],
    cta: "Subscribe Now", popular: true,
  },
  {
    name: "Commerce",
    tagline: "eCommerce store",
    lookup_key: "commerce_monthly",
    price: "$300",
    priceSuffix: "/mo",
    setup: "$1,499 one-time setup",
    note: "Billed monthly · cancel anytime",
    features: ["Full online store", "Secure checkout & payments", "Product & inventory setup", "Local SEO + Google ranking", "Priority support"],
    cta: "Subscribe Now", popular: false,
  },
];

const SERVICES = [
  { icon: Layout, title: "Landing Pages", desc: "High-converting single pages built to turn clicks into customers.", span: "lg:col-span-4" },
  { icon: CreditCard, title: "Business Card Sites", desc: "A clean, professional online presence that earns instant trust.", span: "lg:col-span-4" },
  { icon: ShoppingCart, title: "eCommerce Stores", desc: "Sell online with secure checkout, products & inventory built in.", span: "lg:col-span-4" },
  { icon: Layers, title: "Multipage Websites", desc: "Full-scale sites with everything your growing business needs.", span: "lg:col-span-6" },
  { icon: Search, title: "Local SEO", desc: "Rank on the first page of Google and get found by San Antonio customers every day.", span: "lg:col-span-6" },
  { icon: Sparkles, title: "AI Search Optimization", desc: "Get cited by ChatGPT, Google AI Overviews, Perplexity & Claude. Our next-gen GEO service captures high-intent buyers in the AI era.", span: "lg:col-span-12", featured: true, href: "#ai-seo" },
];

const WORK = [
  { img: "https://images.unsplash.com/photo-1586880244406-556ebe35f282?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Landing Page", title: "Service Business Launch" },
  { img: "https://images.unsplash.com/photo-1539278383962-a7774385fa02?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "eCommerce", title: "Online Retail Store" },
  { img: "https://images.unsplash.com/photo-1625296276397-3bc8839c688f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Multipage", title: "Professional Firm Site" },
  { img: "https://images.pexels.com/photos/6986455/pexels-photo-6986455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tag: "Local SEO", title: "First-Page Google Results" },
];

const PROCESS = [
  { n: "01", title: "Discovery", desc: "We learn your business, goals and the customers you want to reach." },
  { n: "02", title: "Design", desc: "A bold, modern design tailored to your brand — built to convert." },
  { n: "03", title: "Build & Launch", desc: "We develop, test and launch your fast, mobile-ready website." },
  { n: "04", title: "Grow", desc: "Local SEO and ongoing support keep the leads coming in daily." },
];

const WHY = [
  { icon: ShieldCheck, title: "Local & Trusted", desc: "A real San Antonio expert — no offshore call centers, no hidden fees." },
  { icon: Zap, title: "Affordable Plans", desc: "Professional websites priced for small businesses, starting small." },
  { icon: TrendingUp, title: "Built to Convert", desc: "Every site is engineered to bring in new customers, not just look pretty." },
];

const TESTIMONIALS = [
  { quote: "Our new website gets our business on the first page of Google and brings in new customers every day.", name: "Peter S.", loc: "Garland, TX" },
  { quote: "Affordable, professional, and they actually picked up the phone. My bookings doubled in two months.", name: "Maria G.", loc: "San Antonio, TX" },
  { quote: "They made the whole process painless. I finally have a site I'm proud to send to clients.", name: "Derek L.", loc: "Austin, TX" },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`} data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-baseline gap-1 group" data-testid="nav-logo">
          <span className="font-display font-black text-2xl tracking-tighter text-[#1E90FF]">360</span>
          <span className="font-display font-light text-2xl tracking-tighter text-white">WEBCONCEPTS</span>
        </a>
        <nav className="hidden md:flex items-center gap-6">
          {NAV.map((n) => (
            <a key={n.label} href={n.href} className="text-sm text-white/60 hover:text-white transition-colors" data-testid={`nav-link-${n.label.toLowerCase().replace(" ", "-")}`}>{n.label}</a>
          ))}
          <a href="tel:+12103529222" className="flex items-center gap-2 text-sm font-semibold text-white border border-white/20 px-4 py-2.5 rounded-sm hover:border-[#1E90FF] hover:text-[#1E90FF] transition-colors" data-testid="nav-call-button">
            <Phone size={15} className="text-[#1E90FF]" /> (210) 352-9222
          </a>
          <a href="#contact" className="green-glow bg-[#22C55E] text-black font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-[#1fb455] transition-colors" data-testid="nav-cta-button">Get a Free Quote</a>
        </nav>
        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-black/95 border-t border-white/10 px-5 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {NAV.map((n) => (
            <a key={n.label} href={n.href} onClick={() => setOpen(false)} className="text-white/70 hover:text-white">{n.label}</a>
          ))}
          <a href="tel:+12103529222" onClick={() => setOpen(false)} className="flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-5 py-3 rounded-sm" data-testid="mobile-call-button">
            <Phone size={16} className="text-[#1E90FF]" /> (210) 352-9222
          </a>
          <a href="#contact" onClick={() => setOpen(false)} className="bg-[#22C55E] text-black font-semibold text-center px-5 py-3 rounded-sm">Get a Free Quote</a>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <img src="https://images.unsplash.com/photo-1638184984605-af1f05249a56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div initial="hidden" animate="show" className="max-w-4xl">
          <motion.div variants={fadeUp} custom={0} className="flex items-center gap-3 mb-7">
            <span className="h-px w-10 bg-[#1E90FF]" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#1E90FF] font-mono-ibm">San Antonio, TX · Web Design</span>
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} className="font-display font-black text-5xl sm:text-7xl md:text-[5.5rem] leading-[0.95] tracking-tighter">
            Websites that<br />
            <span className="text-white">win you</span> <span className="text-white">customers.</span>
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="mt-8 text-lg sm:text-xl text-white/60 max-w-2xl leading-relaxed">
            Affordable, professional web design for small businesses. We build fast, modern sites engineered to bring you new leads — every single day.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#contact" className="green-glow bg-[#22C55E] text-black font-semibold px-8 py-4 rounded-sm flex items-center justify-center gap-2 hover:bg-[#1fb455] transition-colors" data-testid="hero-cta-button">
              Get My Free Quote <ArrowUpRight size={18} />
            </a>
          </motion.div>
          <motion.div variants={fadeUp} custom={4} className="mt-14 flex flex-wrap gap-x-10 gap-y-4">
            {["First-page Google results", "No hidden fees", "Mobile-ready & fast"].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-white/50">
                <Check size={16} className="text-[#22C55E]" /> {t}
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Landing Pages", "eCommerce", "Local SEO", "Business Sites", "Lead Generation", "Mobile-First", "Fast Hosting"];
  const row = [...items, ...items];
  return (
    <div className="border-y border-white/10 bg-[#111] py-5 overflow-hidden">
      <div className="flex whitespace-nowrap animate-marquee">
        {row.map((t, i) => (
          <span key={i} className="mx-8 text-2xl font-display font-light tracking-tight text-white/40 flex items-center gap-8">
            {t} <span className="text-[#1E90FF]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Section({ id, eyebrow, title, children, className = "" }) {
  return (
    <section id={id} className={`max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 ${className}`}>
      {eyebrow && (
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-10 bg-[#1E90FF]" />
          <span className="text-xs tracking-[0.25em] uppercase text-[#1E90FF] font-mono-ibm">{eyebrow}</span>
        </div>
      )}
      {title && <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter max-w-3xl mb-14">{title}</h2>}
      {children}
    </section>
  );
}

function Services() {
  return (
    <Section id="services" eyebrow="What we build" title="Everything your small business needs to get online.">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const Wrapper = s.href ? "a" : "div";
          return (
            <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className={s.span}>
              <Wrapper {...(s.href ? { href: s.href } : {})}
                className={`group block h-full p-8 sm:p-10 transition-colors ${s.featured ? "bg-gradient-to-r from-[#0d1420] to-[#0d0d0d] hover:from-[#0f1830]" : "bg-[#0d0d0d] hover:bg-[#111]"} ${s.href ? "cursor-pointer" : ""}`}
                data-testid={`service-card-${i}`}>
                {s.featured && <span className="inline-block text-[10px] tracking-[0.2em] uppercase text-[#22C55E] font-mono-ibm border border-[#22C55E]/40 px-2 py-1 mb-5">New · Next-Gen SEO</span>}
                <div className={s.featured ? "flex items-start gap-4" : ""}>
                  <Icon className={`${s.featured ? "text-[#22C55E]" : "text-[#1E90FF]"} mb-6 group-hover:scale-110 transition-transform shrink-0`} size={s.featured ? 34 : 30} />
                  <div>
                    <h3 className={`font-display font-bold tracking-tight mb-3 ${s.featured ? "text-2xl sm:text-3xl flex items-center gap-2" : "text-2xl"}`}>{s.title}{s.featured && <ArrowUpRight size={22} className="text-[#22C55E] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}</h3>
                    <p className="text-white/55 leading-relaxed max-w-2xl">{s.desc}</p>
                  </div>
                </div>
              </Wrapper>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}

function VideoShowcase() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted && v.paused) v.play().catch(() => {});
    setMuted(v.muted);
  };

  return (
    <section id="video" className="border-b border-white/10 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#22C55E]" />
              <span className="text-xs tracking-[0.25em] uppercase text-[#22C55E] font-mono-ibm">Watch</span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter mb-6">Is your website<br />costing you customers?</h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-md mb-8">See exactly how we turn outdated, invisible websites into 24/7 lead machines for local businesses. Tap the video to hear it.</p>
            <a href="#contact" className="green-glow inline-flex items-center gap-2 bg-[#22C55E] text-black font-semibold px-8 py-4 rounded-sm hover:bg-[#1fb455] transition-colors">
              Get My Free Quote <ArrowUpRight size={18} />
            </a>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[360px] border border-white/10 bg-black overflow-hidden rounded-lg group" data-testid="video-showcase">
            <video
              ref={videoRef}
              className="w-full h-auto block cursor-pointer"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="/promo_poster.jpg"
              onClick={toggleMute}
              data-testid="promo-video"
            >
              <source src="/promo_web.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}
              className="absolute bottom-4 right-4 h-11 w-11 grid place-items-center rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white hover:bg-[#22C55E] hover:text-black transition-colors"
              data-testid="video-mute-toggle">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            {muted && (
              <div className="absolute top-4 left-4 text-[11px] font-mono-ibm uppercase tracking-wider bg-black/60 backdrop-blur-md border border-white/20 px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5">
                <Volume2 size={13} className="text-[#22C55E]" /> Tap for sound
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Work() {
  return (
    <Section id="work" eyebrow="Selected work" title="Don't DIY. Let us do it for you.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {WORK.map((w, i) => (
          <motion.div key={w.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
            className="group relative overflow-hidden border border-white/10 bg-[#111]" data-testid={`work-item-${i}`}>
            <div className="overflow-hidden">
              <img src={w.img} alt={w.title} className="w-full h-64 sm:h-72 object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
            </div>
            <div className="p-6 flex items-center justify-between">
              <div>
                <span className="text-xs tracking-[0.2em] uppercase text-[#1E90FF] font-mono-ibm">{w.tag}</span>
                <h3 className="font-display font-bold text-xl mt-1">{w.title}</h3>
              </div>
              <ArrowUpRight className="text-white/40 group-hover:text-[#22C55E] transition-colors" />
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Process() {
  return (
    <Section id="process" eyebrow="How it works" title="From idea to leads in four simple steps.">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {PROCESS.map((p, i) => (
          <motion.div key={p.n} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
            className="bg-[#0d0d0d] p-8 hover:bg-[#111] transition-colors" data-testid={`process-step-${i}`}>
            <span className="font-display font-black text-5xl text-white/10">{p.n}</span>
            <h3 className="font-display font-bold text-xl mt-4 mb-2">{p.title}</h3>
            <p className="text-white/55 text-sm leading-relaxed">{p.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Why() {
  return (
    <section id="why" className="relative overflow-hidden border-y border-white/10">
      <img src="https://images.unsplash.com/photo-1767134426251-789d10a6404f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="San Antonio" className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="flex items-center gap-3 mb-5">
          <MapPin size={16} className="text-[#1E90FF]" />
          <span className="text-xs tracking-[0.25em] uppercase text-[#1E90FF] font-mono-ibm">Proudly San Antonio</span>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter max-w-3xl mb-14">Why local businesses choose 360.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <motion.div key={w.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="border border-white/10 bg-black/40 backdrop-blur-sm p-8" data-testid={`why-card-${i}`}>
                <Icon className="text-[#22C55E] mb-5" size={28} />
                <h3 className="font-display font-bold text-xl mb-2">{w.title}</h3>
                <p className="text-white/55 leading-relaxed">{w.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <Section eyebrow="Client results" title="Real businesses. Real growth.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
            className="border border-white/10 bg-[#111] p-8 flex flex-col" data-testid={`testimonial-${i}`}>
            <div className="flex gap-1 mb-5 text-[#22C55E]">
              {[...Array(5)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}
            </div>
            <p className="text-white/80 text-lg leading-relaxed flex-1">"{t.quote}"</p>
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="font-semibold">{t.name}</p>
              <p className="text-white/40 text-sm">{t.loc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Pricing() {
  const [loadingKey, setLoadingKey] = useState(null);

  const startCheckout = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  return (
    <Section id="pricing" eyebrow="Plans & pricing" title="Simple plans built for small business budgets.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {PLANS.map((p, i) => (
          <motion.div key={p.name} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
            className={`relative flex flex-col p-8 sm:p-10 border ${p.popular ? "border-[#22C55E]/50 bg-[#0f1410]" : "border-white/10 bg-[#0d0d0d]"}`}
            data-testid={`plan-card-${i}`}>
            {p.popular && (
              <span className="absolute -top-3 left-8 bg-[#22C55E] text-black text-[10px] font-semibold tracking-wider uppercase px-3 py-1">Most Popular</span>
            )}
            <span className="text-xs tracking-[0.2em] uppercase text-[#1E90FF] font-mono-ibm">{p.tagline}</span>
            <h3 className="font-display font-black text-3xl tracking-tighter mt-3">{p.name}</h3>
            <div className="mt-5 mb-1 flex items-baseline gap-1">
              <span className="font-display font-black text-4xl tracking-tighter">{p.price}</span>
              {p.priceSuffix && <span className="text-white/40 text-lg">{p.priceSuffix}</span>}
            </div>
            {p.setup && <p className="text-[#1E90FF] text-sm font-medium">+ {p.setup}</p>}
            <p className="text-white/40 text-sm mb-7 mt-1">{p.note}</p>
            <ul className="space-y-3 flex-1 mb-8">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/70 text-sm">
                  <Check size={16} className="text-[#22C55E] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button onClick={() => startCheckout(p.lookup_key)} disabled={loadingKey === p.lookup_key}
              className={`w-full text-center font-semibold px-6 py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60 ${p.popular ? "green-glow bg-[#22C55E] text-black hover:bg-[#1fb455]" : "border border-white/20 text-white hover:bg-white/5"}`}
              data-testid={`plan-cta-${i}`}>
              {loadingKey === p.lookup_key ? "Redirecting..." : <>{p.cta} <ArrowUpRight size={16} /></>}
            </button>
          </motion.div>
        ))}
      </div>
      <p className="text-white/40 text-sm mt-8 text-center">Secure checkout by Stripe. Not sure which plan fits? <a href="#contact" className="text-[#1E90FF] hover:underline">Get a free recommendation</a>.</p>
    </Section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: "How much does a website cost?", a: "Our plans start at $499 for a one-page Starter site, with the Growth plan at $99/month and Commerce stores at $1,499. Every project gets a free, no-obligation quote first — you'll never see a hidden fee." },
    { q: "How long does it take to build my site?", a: "Most Starter and Business Card sites launch in 1–2 weeks. Multipage and eCommerce builds typically take 3–5 weeks depending on content and features. We'll give you a clear timeline after our discovery call." },
    { q: "What is AI Search Optimization (GEO)?", a: "It's next-gen SEO. Instead of chasing keywords, we position your business to be cited directly inside AI answers from Google AI Overviews, ChatGPT, Perplexity and Claude — where high-intent buyers now search first." },
    { q: "Do you help with Google ranking and local SEO?", a: "Yes. Local SEO is built into our process — we optimize your Google Business Profile, reviews, and on-page content so San Antonio customers find you first. AI Search Optimization takes it a step further into generative engines." },
    { q: "Will my website work on phones?", a: "Absolutely. Every site we build is fully responsive and fast on mobile, tablet and desktop — because most of your customers will find you on their phone." },
    { q: "What do I need to provide to get started?", a: "Just a bit about your business and your goals. We handle the design, copy guidance, images, and technical setup. If you have a logo or brand colors, great — if not, we'll help with that too." },
    { q: "Can I update the site myself after launch?", a: "Yes. We can set you up with an easy way to edit content, and our Growth plan includes ongoing updates and support so you're never stuck." },
    { q: "How do payments and cancellations work?", a: "Checkout is handled securely by Stripe. One-time builds are paid upfront; the Growth plan is billed monthly and you can cancel anytime — no contracts." },
  ];
  return (
    <Section id="faq" eyebrow="Questions" title="Frequently asked questions.">
      <div className="max-w-3xl border-t border-white/10" data-testid="faq-list">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="border-b border-white/10" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                data-testid={`faq-question-${i}`}>
                <span className={`font-display font-bold text-lg sm:text-xl tracking-tight transition-colors ${isOpen ? "text-[#22C55E]" : "text-white group-hover:text-white/80"}`}>{f.q}</span>
                <span className={`shrink-0 h-8 w-8 grid place-items-center border transition-all ${isOpen ? "border-[#22C55E] text-[#22C55E] rotate-45" : "border-white/20 text-white/50"}`}>
                  <Plus size={16} />
                </span>
              </button>
              <div className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <p className="text-white/60 leading-relaxed pb-6 pr-14" data-testid={`faq-answer-${i}`}>{f.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-white/40 text-sm mt-10">Still have a question? <a href="#contact" className="text-[#1E90FF] hover:underline">Get in touch</a> or ask our AI assistant in the corner.</p>
    </Section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || "Thanks! We'll be in touch.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black border border-white/15 rounded-sm px-4 py-3.5 text-white placeholder-white/30 focus:border-[#1E90FF] focus:outline-none transition-colors";

  return (
    <section id="contact" className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#22C55E]" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#22C55E] font-mono-ibm">Get started</span>
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-6">Let's build your<br />next customer magnet.</h2>
          <p className="text-white/55 text-lg leading-relaxed mb-10 max-w-md">Tell us about your business and we'll send a free, no-obligation quote within one business day.</p>
          <div className="space-y-5">
            <a href="tel:+12103529222" className="flex items-center gap-4 text-white/70 hover:text-white transition-colors" data-testid="contact-phone">
              <span className="h-11 w-11 grid place-items-center border border-white/15"><Phone size={18} className="text-[#1E90FF]" /></span> (210) 352-9222
            </a>
            <div className="flex items-center gap-4 text-white/70">
              <span className="h-11 w-11 grid place-items-center border border-white/15"><Mail size={18} className="text-[#1E90FF]" /></span> Support@360webconcepts.com
            </div>
            <div className="flex items-center gap-4 text-white/70">
              <span className="h-11 w-11 grid place-items-center border border-white/15"><MapPin size={18} className="text-[#1E90FF]" /></span> San Antonio, Texas
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-white/10 bg-[#0d0d0d] p-8 sm:p-10 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <input className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
            <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
          </div>
          <input className={inputCls} type="email" placeholder="Email address *" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
          <select className={inputCls} value={form.service} onChange={set("service")} data-testid="contact-service-select">
            <option value="">What do you need?</option>
            <option value="Landing Page">Landing Page</option>
            <option value="Business Card Website">Business Card Website</option>
            <option value="eCommerce">eCommerce Store</option>
            <option value="Multipage Website">Multipage Website</option>
            <option value="Local SEO">Local SEO</option>
            <option value="AI Search Optimization">AI Search Optimization (GEO)</option>
            <option value="Not sure yet">Not sure yet</option>
          </select>
          <textarea className={`${inputCls} resize-none`} rows={4} placeholder="Tell us about your project *" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
          <button type="submit" disabled={loading} className="green-glow w-full bg-[#22C55E] text-black font-semibold py-4 rounded-sm hover:bg-[#1fb455] transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="contact-submit-button">
            {loading ? "Sending..." : <>Get My Free Quote <Rocket size={18} /></>}
          </button>
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-baseline gap-1">
          <span className="font-display font-black text-xl tracking-tighter text-[#1E90FF]">360</span>
          <span className="font-display font-light text-xl tracking-tighter">WEBCONCEPTS</span>
        </div>
        <p className="text-white/40 text-sm">© {new Date().getFullYear()} 360 Web Concepts · San Antonio, TX</p>
        <a href="tel:+12103529222" className="text-sm text-white/70 hover:text-white transition-colors flex items-center gap-2" data-testid="footer-phone"><Phone size={14} className="text-[#1E90FF]" /> (210) 352-9222</a>
        <a href="#contact" className="text-sm text-[#22C55E] hover:underline">Start your project →</a>
      </div>
    </footer>
  );
}

function MobileCallBar() {
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-2 gap-px bg-white/10 border-t border-white/10 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-call-bar">
      <a href="tel:+12103529222" className="flex items-center justify-center gap-2 bg-[#0d0d0d] text-white font-semibold py-4 active:bg-[#161616]" data-testid="mobile-bar-call">
        <Phone size={18} className="text-[#1E90FF]" /> Call Now
      </a>
      <a href="#contact" className="flex items-center justify-center gap-2 bg-[#22C55E] text-black font-semibold py-4 active:bg-[#1fb455]" data-testid="mobile-bar-quote">
        <Rocket size={18} /> Free Quote
      </a>
    </div>
  );
}

export default function LandingPage() {
  return (
    <main className="bg-[#0a0a0a] text-white overflow-x-hidden pb-16 md:pb-0">
      <Nav />
      <Hero />
      <Marquee />
      <VideoShowcase />
      <Services />
      <AISearchSection />
      <Work />
      <Process />
      <Why />
      <Testimonials />
      <Pricing />
      <FAQ />
      <Contact />
      <Footer />
      <MobileCallBar />
    </main>
  );
}
